import { Platform } from "react-native";
import type { AuthUser } from "../../runtime/ports";
import { purchaseRpcTransport, sellerLoginRpc } from "@libs/buy-on-behalf-client/purchase-rpc";
import type { PurchaseTransport } from "@libs/buy-on-behalf-client/purchase";
import { auth } from "@utils/firebase";
import config from "@utils/config";
import { getCountryCode } from "@utils/region";
import { realtimeAttemptChanges } from "@utils/realtime/attemptChanges";
import { vendorHeaders } from "./vendorAuth";

/** Where every purchase the app starts comes from, natively and on
 *  app-web alike (one module for both). */
const appOrigin = () =>
  ({
    source: "lissen_app",
    os: Platform.OS === "ios" ? "ios" : Platform.OS === "android" ? "android" : "web",
  }) as const;

/** My Tickets' seller-login reveal for one sign-in (`sellerLoginRpc`);
 *  replace it when the buyer changes. */
export const createSellerLogin = (
  buyer: AuthUser | null,
  current: () => AuthUser | null = () => auth.currentUser,
) =>
  sellerLoginRpc({
    url: `${config.API_URL}/api/trpc/buy-on-behalf`,
    headers: vendorHeaders(buyer, current, getCountryCode),
  });

/**
 * The purchase sheet's calls for one sign-in (LIS-1173), over the api's
 * buy-on-behalf router: the signed-in read of the purchase is the host's
 * (`read`, the access read), and every start is the app's (`origin`) in
 * the sheet's purchase funnel (`funnelId`). A watched attempt is read again
 * as the app's realtime socket says it changed, and polled as the fallback.
 * Keep it for one sign-in and one funnel; replace it when either changes.
 */
export const createPurchaseTransport = (
  buyer: AuthUser | null,
  current: () => AuthUser | null,
  read: PurchaseTransport["read"],
  funnelId: string,
): PurchaseTransport =>
  purchaseRpcTransport({
    url: `${config.API_URL}/api/trpc/buy-on-behalf`,
    headers: vendorHeaders(buyer, current, getCountryCode),
    read,
    start: () => ({ origin: appOrigin(), funnelId }),
    changes: realtimeAttemptChanges,
  });
